import { useCallback, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import FullCalendar from '@fullcalendar/react'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin from '@fullcalendar/interaction'
import thLocale from '@fullcalendar/core/locales/th'
import type {
  DateSelectArg,
  EventClickArg,
  EventDropArg,
  EventInput,
} from '@fullcalendar/core'
import type { EventResizeDoneArg } from '@fullcalendar/interaction'
import AppointmentModal, { type AppointmentModalState } from '@/components/AppointmentModal'
import {
  APPOINTMENT_STATUS_COLORS,
  createAppointment,
  listAppointments,
  updateAppointment,
  type Appointment,
  type AppointmentCreatePayload,
  type AppointmentUpdatePayload,
} from '@/lib/appointments'
import { toastError, toastSuccess } from '@/store/toast'

export default function CalendarPage() {
  const queryClient = useQueryClient()
  const [range, setRange] = useState<{ from: string; to: string } | null>(null)
  const [modal, setModal] = useState<AppointmentModalState>(null)
  const calendarRef = useRef<FullCalendar>(null)

  const { data } = useQuery({
    queryKey: ['appointments', range?.from, range?.to],
    queryFn: () => listAppointments(range!.from, range!.to),
    enabled: !!range,
    refetchOnWindowFocus: true,
  })

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['appointments'] })

  const createMut = useMutation({
    mutationFn: (payload: AppointmentCreatePayload) => createAppointment(payload),
    onSuccess: () => {
      invalidate()
      setModal(null)
      toastSuccess('สร้างนัดแล้ว')
    },
    onError: (e: Error) => toastError(`สร้างนัดไม่สำเร็จ: ${e.message}`),
  })

  const updateMut = useMutation({
    mutationFn: ({ id, patch }: { id: number; patch: AppointmentUpdatePayload }) =>
      updateAppointment(id, patch),
    onSuccess: () => {
      invalidate()
      setModal(null)
      toastSuccess('บันทึกแล้ว')
    },
    onError: (e: Error) => toastError(`บันทึกไม่สำเร็จ: ${e.message}`),
  })

  const events: EventInput[] = useMemo(
    () =>
      (data?.appointments ?? [])
        // "ลบนัด" sets status=cancelled; the row stays in the DB
        // (Nothing is Deleted) but disappears from the calendar.
        .filter((a) => a.status !== 'cancelled')
        .map((a) => ({
          id: String(a.id),
          title: `${a.patientName} · ${a.therapistName}`,
          start: a.startAt,
          end: a.endAt,
          backgroundColor: APPOINTMENT_STATUS_COLORS[a.status],
          borderColor: APPOINTMENT_STATUS_COLORS[a.status],
          extendedProps: { appointment: a },
        })),
    [data],
  )

  const onSelect = useCallback((arg: DateSelectArg) => {
    calendarRef.current?.getApi().unselect()
    setModal({ mode: 'create', start: arg.start, end: arg.end })
  }, [])

  // "+ สร้างนัด" button — no dragging needed. Defaults to the next free
  // 30-min slot from now, clamped into the 08:00–19:30 bookable window.
  const openCreateNow = useCallback(() => {
    const start = new Date()
    start.setSeconds(0, 0)
    const m = start.getMinutes()
    start.setMinutes(m === 0 ? 0 : m <= 30 ? 30 : 60)
    if (start.getHours() < 8) start.setHours(8, 0, 0, 0)
    if (start.getHours() >= 20 || (start.getHours() === 19 && start.getMinutes() > 30)) {
      start.setHours(9, 0, 0, 0)
    }
    const end = new Date(start.getTime() + 30 * 60 * 1000)
    setModal({ mode: 'create', start, end })
  }, [])

  const onEventClick = useCallback((arg: EventClickArg) => {
    const appointment = arg.event.extendedProps.appointment as Appointment
    setModal({ mode: 'edit', appointment })
  }, [])

  // Drag-move / drag-resize → PATCH; revert the event on failure. Bypasses
  // the mutation's onSuccess (which would close a modal that was never open).
  const onEventChange = useCallback(
    (arg: EventDropArg | EventResizeDoneArg) => {
      const { event, revert } = arg
      if (!event.start || !event.end) return revert()
      updateAppointment(Number(event.id), {
        startAt: event.start.toISOString(),
        endAt: event.end.toISOString(),
      })
        .then(() => invalidate())
        .catch((e: Error) => {
          revert()
          toastError(`ย้ายนัดไม่สำเร็จ: ${e.message}`)
        })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  return (
    <div className="p-4">
      <div className="mb-3 flex justify-end">
        <button
          type="button"
          onClick={openCreateNow}
          className="rounded-lg bg-[#204687] px-4 py-2 text-sm font-medium text-white hover:bg-[#1a3a70]"
        >
          + สร้างนัด
        </button>
      </div>
      <FullCalendar
        ref={calendarRef}
        plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
        initialView={window.innerWidth < 768 ? 'timeGridDay' : 'timeGridWeek'}
        headerToolbar={{
          left: 'prev,next today',
          center: 'title',
          right: 'dayGridMonth,timeGridWeek,timeGridDay',
        }}
        locale={thLocale}
        height="auto"
        slotMinTime="08:00:00"
        slotMaxTime="20:00:00"
        slotDuration="00:30:00"
        snapDuration="00:30:00"
        allDaySlot={false}
        nowIndicator
        selectable
        selectMirror
        editable
        eventStartEditable
        eventDurationEditable
        longPressDelay={300}
        events={events}
        datesSet={(arg) => setRange({ from: arg.start.toISOString(), to: arg.end.toISOString() })}
        select={onSelect}
        eventClick={onEventClick}
        eventDrop={onEventChange}
        eventResize={onEventChange}
      />
      <AppointmentModal
        state={modal}
        onClose={() => setModal(null)}
        onCreate={(payload) => createMut.mutate(payload)}
        onUpdate={(id, patch) => updateMut.mutate({ id, patch })}
        saving={createMut.isPending || updateMut.isPending}
      />
    </div>
  )
}
